import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { fetchMergedDailyServiceReports, ReportFilterCriteria } from '../../graphql/reportQueries';
import { ReportFilterBar, ReportFilterValues, UnitReportScope } from './ReportFilterBar';
import { ReportExportMenu } from './ReportExportMenu';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import type { NormalizedReportRecord, ReportConfiguration, AggregatedDataPoint } from '../../types/reportTypes';
import { buildReportPath } from '../../utils/reportUrlUtils';
import {
    buildCompanyReportsPath,
    buildUnitReportsPath,
    type ReportDetailReturnState,
} from '../../utils/unitReportsUtils';
import '../../css/DailyServiceLogsTab.css';

interface DailyServiceLogsTabProps {
    companyId: string;
    unitScope?: UnitReportScope;
}

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Daily Service Logs tab content.
 * Fetches merged daily service reports (new + legacy), displays in a table
 * with filtering, pagination, row click navigation, and export.
 *
 * Validates: Requirements 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 5.8, 9.6
 */
export const DailyServiceLogsTab = ({ companyId, unitScope }: DailyServiceLogsTabProps) => {
    const navigate = useNavigate();
    const tableRef = useRef<HTMLDivElement>(null);
    const { companyLocations } = useRegionAndLocationContext();

    const [records, setRecords] = useState<NormalizedReportRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const STORAGE_KEY = 'report-filters-daily-service';

    // Default to last 30 days for initial load performance
    const defaultStart = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, []);

    // Initialize from sessionStorage so the first fetch uses restored filters
    const [filters, setFilters] = useState<ReportFilterValues>(() => {
        try {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            if (saved) return JSON.parse(saved) as ReportFilterValues;
        } catch { /* ignore */ }
        return {
            equipmentCategoryId: '',
            unitId: '',
            dateStart: defaultStart,
            dateEnd: '',
            locationId: '',
            districtId: '',
            regionId: '',
        };
    });

    // Fetch reports when companyId or date filters change (server-side = date range only)
    useEffect(() => {
        if (!companyId) return;
        setIsLoading(true);
        setError(null);

        const criteria: ReportFilterCriteria = {};
        if (filters.dateStart) criteria.dateStart = filters.dateStart;
        if (filters.dateEnd) criteria.dateEnd = filters.dateEnd;

        fetchMergedDailyServiceReports(companyId, criteria)
            .then((items) => setRecords(items))
            .catch((err) => {
                console.error('Failed to fetch daily service logs:', err);
                Sentry.captureException(err);
                setError('Failed to load daily service logs. Please try again.');
            })
            .finally(() => setIsLoading(false));
    }, [companyId, filters.dateStart, filters.dateEnd]);

    // All non-date filtering happens client-side so it's instant
    const filteredRecords = useMemo(() => {
        let result = records;

        if (filters.equipmentCategoryId) {
            result = result.filter((r) => r.equipmentCategoryId === filters.equipmentCategoryId);
        }
        if (filters.unitId) {
            result = result.filter((r) => r.unitId === filters.unitId);
        }
        if (filters.regionId) {
            const regionLocationIds = new Set(
                companyLocations
                    .filter((loc) => loc.regionalDivisionId === filters.regionId)
                    .map((loc) => loc.id)
            );
            result = result.filter((r) => r.locationId && regionLocationIds.has(r.locationId));
        }
        if (filters.locationId) {
            result = result.filter((r) => r.locationId === filters.locationId);
        }

        return result;
    }, [records, filters.equipmentCategoryId, filters.unitId, filters.regionId, filters.locationId, companyLocations]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
    const paginatedRecords = filteredRecords.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
    );

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    const handleFilterChange = (newFilters: ReportFilterValues) => {
        setFilters(newFilters);
    };

    const handleRowClick = (record: NormalizedReportRecord) => {
        const source = record.source === 'LegacyReport' ? 'LegacyReport' : 'DailyServiceReport';
        const returnState: ReportDetailReturnState = unitScope
            ? {
                  returnTo: buildUnitReportsPath(unitScope.unitId, {
                      tab: 'daily-service',
                      equipmentCategoryId: unitScope.equipmentCategoryId,
                  }),
                  returnLabel: 'Back to Reports',
              }
            : {
                  returnTo: buildCompanyReportsPath({ tab: 'daily-service' }),
                  returnLabel: 'Back to Reports',
              };
        navigate(
            buildReportPath({
                id: record.id,
                source,
                slugLabel: record.unitName || record.equipmentCategoryName,
            }),
            { state: returnState },
        );
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(1);
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    const formatDuration = (seconds?: number) => {
        if (seconds == null) return '—';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins === 0) return `${secs}s`;
        return `${mins}m ${secs}s`;
    };

    // Build export data from filtered records
    const exportData: AggregatedDataPoint[] = filteredRecords.map((r) => ({
        groupKey: r.id,
        groupLabel: r.equipmentCategoryName || r.id,
        value: r.okCount + r.warnCount + r.inopCount,
        count: 1,
    }));

    const exportConfig: ReportConfiguration = {
        metricKey: 'daily-service-logs',
        dateRangeStart: filters.dateStart || '',
        dateRangeEnd: filters.dateEnd || '',
        groupByField: 'asset',
        filters: [],
        visualizationType: 'table',
    };

    // Generate page numbers with ellipsis for large page counts
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    if (isLoading) {
        return <div className="dsl-loading">Loading daily service logs...</div>;
    }

    if (error) {
        return (
            <div className="dsl-empty">
                <div className="dsl-empty-title">{error}</div>
                <button
                    className="dsl-pagination-btn"
                    style={{ marginTop: 12 }}
                    onClick={() => setFilters({ ...filters })}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="dsl-tab" ref={tableRef}>
            <ReportFilterBar
                onFilterChange={handleFilterChange}
                companyId={companyId}
                initialFilters={{ dateStart: defaultStart, ...unitScope }}
                storageKey={STORAGE_KEY}
                scopeFilters={unitScope}
            />

            <div className="dsl-header">
                <h3>Daily Service Logs ({filteredRecords.length})</h3>
                <div className="dsl-header-actions">
                    <ReportExportMenu
                        data={exportData}
                        config={exportConfig}
                        reportName="Daily Service Logs"
                        targetRef={tableRef}
                    />
                </div>
            </div>

            {filteredRecords.length === 0 ? (
                <div className="dsl-empty">
                    <div className="dsl-empty-icon">🔧</div>
                    <div className="dsl-empty-title">No daily service logs found</div>
                    <div className="dsl-empty-text">
                        Try adjusting your filters or check back later.
                    </div>
                </div>
            ) : (
                <>
                    <div className="dsl-table-wrapper">
                        <table className="dsl-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Equipment Category</th>
                                    <th>Unit</th>
                                    <th>Technician</th>
                                    <th>OK / Warn / Inop</th>
                                    <th>Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedRecords.map((record) => (
                                    <tr key={record.id} onClick={() => handleRowClick(record)}>
                                        <td>{formatDate(record.createdAt)}</td>
                                        <td>{record.equipmentCategoryName || '—'}</td>
                                        <td>{record.unitName || '—'}</td>
                                        <td>{record.userName || '—'}</td>
                                        <td>
                                            <div className="dsl-status-counts">
                                                <span className="dsl-count-badge ok">{record.okCount}</span>
                                                <span className="dsl-count-badge warn">{record.warnCount}</span>
                                                <span className="dsl-count-badge inop">{record.inopCount}</span>
                                            </div>
                                        </td>
                                        <td>{formatDuration(record.inspectionDurationSeconds)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="dsl-pagination">
                        <div className="dsl-pagination-info">
                            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredRecords.length)} of {filteredRecords.length}
                            <select
                                className="dsl-page-size-select"
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                aria-label="Page size"
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>{size} per page</option>
                                ))}
                            </select>
                        </div>
                        <div className="dsl-pagination-controls">
                            <button
                                className="dsl-pagination-btn"
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage((p) => p - 1)}
                            >
                                ‹ Prev
                            </button>
                            {getPageNumbers().map((page, idx) =>
                                typeof page === 'string' ? (
                                    <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>
                                ) : (
                                    <button
                                        key={page}
                                        className={`dsl-pagination-btn ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                ),
                            )}
                            <button
                                className="dsl-pagination-btn"
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage((p) => p + 1)}
                            >
                                Next ›
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
